/**
 * <combined-listing-card>
 *
 * Wraps the colour swatches of a Combined Listing on a product card.
 * Each colour is a SEPARATE product, so selecting one points the card's
 * existing quick-add at that sibling product's variant — mirroring how the
 * theme's normal colour swatches select a variant for quick-add (no navigation).
 *
 * Behaviour:
 *  - hover / focus a swatch  → preview that colour's image on the card
 *  - leave                   → restore the selected (or original) image
 *  - click a swatch          → lock selection: swap image, re-point the card's
 *                              quick-add to the sibling variant, update price,
 *                              link and sold-out state
 *
 * Sibling images come from Liquid when a variant image exists; otherwise we
 * lazily fetch the sibling product's featured image from its `.js` endpoint.
 *
 * No globals, listeners cleaned up on disconnect.
 */
if (!customElements.get('combined-listing-card')) {
  customElements.define(
    'combined-listing-card',
    class CombinedListingCard extends HTMLElement {
      connectedCallback() {
        this.card = this.closest('product-item-card') || this.closest('.grid-product');
        if (!this.card) return;

        this.image = this.card.querySelector('.grid-product__image-mask img');
        this.link = this.card.querySelector('.grid-product__link');
        this.priceWrapper = this.card.querySelector('.grid-product__price');
        this.atc = this.card.querySelector('.atc-btn.add-to-cart');
        this.soldOut = this.card.querySelector('.atc-btn.soldout');
        this.badges = this.card.querySelector('.grid-product__badges');
        if (!this.image) return;

        this.originalSrc = this.image.getAttribute('src');
        this.originalSrcset = this.image.getAttribute('srcset');
        this.originalHref = this.link ? this.link.getAttribute('href') : null;
        this.lockedSrc = null; // image of the currently selected colour
        this.cache = new Map(); // product url → fetched { image, price, available }

        this.onPreview = this.onPreview.bind(this);
        this.onRestore = this.onRestore.bind(this);
        this.onSelect = this.onSelect.bind(this);
        this.onPrefetch = this.onPrefetch.bind(this);

        this.addEventListener('pointerover', this.onPreview);
        this.addEventListener('focusin', this.onPreview);
        this.addEventListener('pointerleave', this.onRestore);
        this.addEventListener('focusout', this.onRestore);
        this.addEventListener('click', this.onSelect);
        // Warm the image cache as soon as a press starts (covers touch, which
        // has no hover) so the click feels instant.
        this.addEventListener('pointerdown', this.onPrefetch);
      }

      disconnectedCallback() {
        this.removeEventListener('pointerover', this.onPreview);
        this.removeEventListener('focusin', this.onPreview);
        this.removeEventListener('pointerleave', this.onRestore);
        this.removeEventListener('focusout', this.onRestore);
        this.removeEventListener('click', this.onSelect);
        this.removeEventListener('pointerdown', this.onPrefetch);
      }

      onPrefetch(event) {
        const swatch = this.swatchFrom(event);
        if (swatch) this.resolveImage(swatch); // fire-and-forget, caches result
      }

      swatchFrom(event) {
        return event.target.closest('.trigger-update-variant-combined');
      }

      setImage(src, srcset) {
        if (!src) return;
        this.image.setAttribute('src', src);
        if (srcset) {
          this.image.setAttribute('srcset', srcset);
        } else {
          this.image.removeAttribute('srcset');
        }
      }

      /* Resolve a swatch's preview image, fetching the sibling product's
         featured image if Liquid couldn't provide a variant image. */
      async resolveImage(swatch) {
        if (swatch.dataset.variantImage) {
          return { src: swatch.dataset.variantImage, srcset: swatch.dataset.variantImageSrcset };
        }
        const url = swatch.dataset.productUrl;
        if (!url) return null;
        if (!this.cache.has(url)) {
          try {
            const res = await fetch(`${url}.js`);
            const data = await res.json();
            const base = data.featured_image || (data.images && data.images[0]);
            this.cache.set(url, base ? { src: `${base}${base.includes('?') ? '&' : '?'}width=540` } : null);
          } catch (e) {
            this.cache.set(url, null);
          }
        }
        return this.cache.get(url);
      }

      async onPreview(event) {
        const swatch = this.swatchFrom(event);
        if (!swatch) return;
        const img = await this.resolveImage(swatch);
        if (img) this.setImage(img.src, img.srcset);
      }

      onRestore(event) {
        if (event.type === 'focusout' && this.contains(event.relatedTarget)) return;
        if (this.lockedSrc) {
          this.setImage(this.lockedSrc.src, this.lockedSrc.srcset);
        } else {
          this.setImage(this.originalSrc, this.originalSrcset);
        }
      }

      async onSelect(event) {
        const swatch = this.swatchFrom(event);
        if (!swatch) return;
        event.preventDefault();

        // --- Instant updates (no network) so selection + quick-add feel immediate ---

        // Visual selection state
        this.querySelectorAll('.color-swatch--selected').forEach((el) =>
          el.classList.remove('color-swatch--selected'),
        );
        swatch.classList.add('color-swatch--selected');

        // Re-point the card's quick-add at the sibling product's variant
        const variantId = swatch.dataset.variantId;
        if (variantId && this.card) {
          this.card.dataset.variantId = variantId;
          // product-item-card.addToCartGrid() reads the instance property
          if ('selectedVariant' in this.card) this.card.selectedVariant = variantId;
        }

        // Card link → selected colour's product
        if (this.link && swatch.dataset.productUrl) {
          this.link.setAttribute('href', swatch.dataset.productUrl);
        }

        // Price
        if (this.priceWrapper && swatch.dataset.price) {
          this.priceWrapper.textContent = swatch.dataset.price;
        }

        // Sold-out state
        const available = swatch.dataset.available !== 'false';
        if (this.atc && this.soldOut) {
          this.atc.classList.toggle('hide', !available);
          this.soldOut.classList.toggle('hide', available);
        }

        // Badge slot: sold-out outranks every other badge (see product-card-badges.liquid).
        // Both slots are rendered server-side; we only flip which one is visible.
        // If there is no sold-out slot (enable_badge off) leave `other` alone.
        if (this.badges) {
          const soldOutSlot = this.badges.querySelector('[data-cl-badge="sold-out"]');
          const otherSlot = this.badges.querySelector('[data-cl-badge="other"]');
          if (soldOutSlot) {
            soldOutSlot.hidden = available;
            if (otherSlot) otherSlot.hidden = !available;
          }
        }

        // --- Image swap (may need a network fetch) with a loading state ---
        const mask = this.image.closest('.grid-product__image-mask');
        if (mask) mask.classList.add('cl-image-loading');
        const img = await this.resolveImage(swatch);
        if (img) {
          this.lockedSrc = img;
          this.setImage(img.src, img.srcset);
        }
        if (mask) mask.classList.remove('cl-image-loading');
      }
    },
  );
}
